import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { LegalHeader } from '@/components/legal/LegalDocument';

const STORAGE_KEY = 'cookie_consent';
type ConsentValue = 'all' | 'essential';

const categories = [
  {
    id: 'essential',
    name: 'Essential Cookies',
    description:
      'Required for the platform to function. Includes session authentication, security tokens, and UI preference storage. Cannot be disabled.',
    required: true,
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    description:
      'Help us understand how users interact with admeru so we can improve the product. Data is aggregated and anonymised. We use Google Analytics.',
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing Cookies',
    description:
      'Used to measure the effectiveness of our campaigns and show relevant content to prospective users on third-party platforms.',
    required: false,
  },
];

export const CookieSettingsPage: React.FC = () => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ConsentValue | null;
    if (stored === 'all') {
      setAnalyticsEnabled(true);
      setMarketingEnabled(true);
    }
  }, []);

  const save = () => {
    const value: ConsentValue =
      analyticsEnabled || marketingEnabled ? 'all' : 'essential';
    localStorage.setItem(STORAGE_KEY, value);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const toggleCategory = (id: string) => {
    if (id === 'analytics') setAnalyticsEnabled((v) => !v);
    if (id === 'marketing') setMarketingEnabled((v) => !v);
  };

  const isEnabled = (id: string) => {
    if (id === 'essential') return true;
    if (id === 'analytics') return analyticsEnabled;
    if (id === 'marketing') return marketingEnabled;
    return false;
  };

  return (
    <div className="min-h-[100dvh] bg-card text-ink">
      <LegalHeader title="Cookie settings" meta="Your choice is saved in this browser. Change it any time." />
      <div className="mx-auto flex max-w-[1120px] flex-col gap-6 px-5 py-10 md:px-10">
        <div className="flex max-w-[760px] flex-col">
          {categories.map((cat, i) => {
            const enabled = isEnabled(cat.id);
            return (
              <div key={cat.id} className={`grid gap-3 py-5 sm:grid-cols-[1fr_auto] sm:gap-8 ${i === 0 ? 'border-t-[1.5px] border-ink' : 'border-t border-line'}`}>
                <div className="flex flex-col gap-1">
                  <span className="flex items-center gap-2 font-display text-[17px] font-bold">
                    {cat.name.replace(' Cookies', ' cookies')}{cat.required && <span className="rounded-full bg-inset px-2.5 py-0.5 text-xs font-semibold text-ink-2">Always on</span>}
                  </span>
                  <span className="text-[15px] leading-relaxed text-ink-2">{cat.description}</span>
                </div>
                <button type="button" role="switch" aria-checked={enabled} aria-label={cat.name} disabled={cat.required} onClick={() => toggleCategory(cat.id)}
                  className={`mt-1 h-7 w-12 shrink-0 rounded-full p-0.5 transition-colors ${enabled ? 'bg-navy' : 'bg-line-2'} ${cat.required ? 'cursor-not-allowed opacity-70' : ''}`}>
                  <span className={`block h-6 w-6 rounded-full bg-card shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-5">
            <button type="button" onClick={() => { setAnalyticsEnabled(false); setMarketingEnabled(false); }}
              className="min-h-[44px] rounded-btn px-4 font-display text-sm font-bold text-ink shadow-ring-line hover:shadow-ring-ink">Needed only</button>
            <button type="button" onClick={() => { setAnalyticsEnabled(true); setMarketingEnabled(true); }}
              className="min-h-[44px] rounded-btn px-4 font-display text-sm font-bold text-ink shadow-ring-line hover:shadow-ring-ink">Allow all</button>
            <button type="button" onClick={save}
              className="ml-auto min-h-[44px] rounded-btn bg-navy px-5 font-display text-sm font-bold text-on-navy">Save preferences</button>
          </div>
          {saved && <p className="m-0 mt-3 flex items-center gap-2 text-sm text-ok"><Check className="h-4 w-4" /> Preferences saved.</p>}
          <p className="m-0 mt-6 text-[15px] text-ink-2">
            More in the <a href="/cookie-policy" className="font-semibold text-brand-ink underline underline-offset-2">Cookie policy</a>. Questions? <a href="mailto:privacy@admeru.com" className="font-semibold text-brand-ink underline underline-offset-2">privacy@admeru.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};
